import {announce} from '@github-ui/aria-live'
import {getInputRequests} from '@github-ui/ahp-relay/ahp-session-selectors'
import type {ActivePendingToolConfirmation} from '@github-ui/ahp-relay/ahp-session-selectors'
import {
  ahpCompletionFromResponse,
  ahpInputRequestToPrompt,
} from '@github-ui/copilot-shared-ui/ahp/ahp-prompt-conversion'
import type {CommandType} from '@github-ui/copilot-shared-ui/ahp/command-types'
import type {ChatInputRequest, ChatState} from '@microsoft/agent-host-protocol'
import {useCallback, useState} from 'react'
import type {EstablishedAhpSession} from '@github-ui/ahp-session-client/contracts'

interface PendingPromptSelection {
  pendingInputRequest: ChatInputRequest | undefined
  pendingPrompt: ReturnType<typeof ahpInputRequestToPrompt>
}

function selectPendingPrompt(
  state: ChatState | undefined,
  activeConfirmation: ActivePendingToolConfirmation | undefined,
): PendingPromptSelection {
  if (!state || activeConfirmation || (state.activeTurn && !Array.isArray(state.activeTurn.responseParts))) {
    return {pendingInputRequest: undefined, pendingPrompt: null}
  }

  const selection = getInputRequests(state)
    .map(request => ({pendingInputRequest: request, pendingPrompt: ahpInputRequestToPrompt(request)}))
    .find(({pendingPrompt}) => pendingPrompt !== null)

  return selection ?? {pendingInputRequest: undefined, pendingPrompt: null}
}

export function usePendingPromptTakeover({
  state,
  activeConfirmation,
  establishedSession,
}: {
  state: ChatState | undefined
  activeConfirmation: ActivePendingToolConfirmation | undefined
  establishedSession?: EstablishedAhpSession
}) {
  const [submittingInputRequestId, setSubmittingInputRequestId] = useState<string | null>(null)

  const {pendingInputRequest, pendingPrompt} = selectPendingPrompt(state, activeConfirmation)

  const handlePromptRespond = useCallback(
    (toolCallId: string, response: unknown, commandType: CommandType): boolean => {
      if (!pendingInputRequest || pendingInputRequest.id !== toolCallId || submittingInputRequestId === toolCallId) {
        return false
      }

      const params = ahpCompletionFromResponse(pendingInputRequest, response, commandType)
      if (!params) return false

      setSubmittingInputRequestId(toolCallId)
      try {
        if (!establishedSession) throw new Error('An established AHP session is required to respond to this prompt.')
        establishedSession.commands.respondToPrompt(params)
        return true
      } catch {
        setSubmittingInputRequestId(null)
        announce('Your response could not be sent. Please try again.')
        return false
      }
    },
    [establishedSession, pendingInputRequest, submittingInputRequestId],
  )

  return {
    pendingPrompt,
    handlePromptRespond,
    isSubmittingPrompt: submittingInputRequestId === pendingInputRequest?.id,
  }
}
